import type { MissingFieldsData } from '@/state/navigation';
import type {
  Card,
  CoverSheetConfig,
  Field,
} from '@/views/Coversheet/configurations/types';
import { getInvalidCardFields } from '@/views/Coversheet/hooks/useFieldValidator';
import { doesFieldValidationApply } from './fieldValidation';
import { indexifyValues } from './indexifyValues';
import type { SanitizedCoverSheetFormData } from './transformCoverSheetToFormData';

const CARD_INDEX = 0;

const isFieldRequired = (field: Field, index: number) =>
  !!field.validation?.some(
    (v) => v.type === 'required' && doesFieldValidationApply(v, index),
  );

const getParentField = (card: Card, field: Field): Field | null => {
  if (!card.fields?.length) return null;
  if (field.showWhen) {
    const { fieldName } = field.showWhen;
    return card.fields.find((f) => f.name === fieldName) ?? null;
  }
  return (
    card.fields.find(
      (f) => f.name !== field.name && f.customField?.name === field.name,
    ) ?? null
  );
};

/**
 * Walks the cover-sheet config + form data and returns the same
 * `name -> MissingFieldsData` map that mounted field validators would build
 * up at runtime. Lets the missing-fields atom be populated from any route,
 * not only when the Cover Sheet page is mounted.
 */
export function computeMissingFields(
  config: CoverSheetConfig,
  formData: SanitizedCoverSheetFormData,
): Record<string, MissingFieldsData> {
  const missing: Record<string, MissingFieldsData> = {};

  config.sections.forEach((section) => {
    section.cards.forEach((card) => {
      const indexedCard = indexifyValues<Card>(card, CARD_INDEX);
      const invalidFields = getInvalidCardFields(
        indexedCard,
        formData as Record<string, unknown>,
        CARD_INDEX,
      );

      invalidFields.forEach((field) => {
        if (!isFieldRequired(field, CARD_INDEX)) return;
        const parent = getParentField(indexedCard, field);
        missing[field.name] = {
          name: field.name,
          label: parent?.label ?? field.label,
          index: CARD_INDEX,
          section: section.sidebarText,
          cardId: card.id,
          cardTitle: card.innerTitle ?? undefined,
        };
      });
    });
  });

  return missing;
}
